import { router } from 'expo-router';
import { ChevronRight } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { ActivityIndicator, Pressable, RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { DealAvailabilityBar } from '@/components/AvailabilityBar';
import { BrandLogo } from '@/components/BrandLogo';
import { BrandTile } from '@/components/BrandTile';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { ProgramCard } from '@/components/ProgramCard';
import { ProofTiles } from '@/components/ProofTiles';
import { QrSheet } from '@/components/QrSheet';
import { StoryCard } from '@/components/StoryCard';
import { Toast } from '@/components/Toast';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { formatMoney } from '@/lib/money';
import { useShareLink, type ShareProgram } from '@/hooks/useShareLink';
import { buildInviteMessage, buildShareMessage } from '@/lib/shareLinks';
import { dealsStore } from '@/stores/deals.store';
import { referralLinksStore } from '@/stores/referralLinks.store';
import { referralStatsStore } from '@/stores/referralStats.store';
import { referralsStore } from '@/stores/referrals.store';
import type { Deal } from '@/types/deal';

/**
 * Refer tab — v5 (2026-09-17, Jason's direction): proof first, then the
 * programs. One card per referable deal (SoFi Checking's cut stays in the
 * #1 spot — locked decision) plus the app-invite card. Tapping a card's
 * header opens its detail:
 *
 *   deal card   → /referrals/deal/[id]  (hero, friend progress, nudges)
 *   invite card → /referrals/invite     (generic link, how-it-works)
 *
 * What v5 changed, and why:
 *
 *   - The dark earnings hero became a link in the header. A balance the
 *     Home tab already shows was the largest thing on a screen whose job
 *     is to get a link shared, and it read as $0 for every new ambassador
 *     — the exact person this screen has to convince.
 *   - A story card took the dark slot instead. Someone the user recognises
 *     saying what they made outsells their own empty balance.
 *   - Program cards lost their eyebrow, headline and body paragraph. With
 *     the story carrying the persuasion, three layers of selling per card
 *     was the same argument three times, and it pushed the second program
 *     below the fold.
 *
 * v4 (2026-08-24) is what introduced the index itself, replacing v3's
 * deal-picker pills: with two different referral products on one screen
 * the pills conflated "which deal am I sharing" with "which program is
 * this", and the nudge/feed stack belongs to the deal context, not here.
 */

/**
 * What a card prints for each side. `display_*` arrives with the invite
 * program backend and folds in brand-funded top-ups; until then (and on any
 * older server) the effective cut is the truth. Optional fields, so a card
 * can never render NaN.
 */
function referrerCents(deal: Deal): number {
  return deal.display_referrer_cents ?? deal.referrer_cut_cents;
}
function userCents(deal: Deal): number {
  return deal.display_user_cents ?? deal.user_cut_cents;
}

/** Sentinel id for the invite program, which has no deal behind it. */
const INVITE_ID = -1;



const ReferralsTab = observer(() => {
  const { toast, qr, sharingId, share, copy, showQr, closeQr } = useShareLink();

  useEffect(() => {
    void dealsStore.ensureLoaded();
    void referralsStore.ensureLoaded();
    void referralStatsStore.ensureLoaded();
    void referralLinksStore.ensureLoaded();
  }, []);

  const onRefresh = useCallback(() => {
    // The PULL variant: `refreshList` is the throttled focus refresher and
    // no-ops inside its 30s window, which is exactly when someone pulls to
    // see a deal go full or reopen.
    void dealsStore.pullRefreshList();
    void referralsStore.refresh().catch(() => {});
    void referralStatsStore.refresh().catch(() => {});
  }, []);

  const openDeal = useCallback((deal: Deal) => {
    router.push({ pathname: '/referrals/deal/[id]', params: { id: String(deal.id) } });
  }, []);

  const openInvite = useCallback(() => {
    router.push('/referrals/invite');
  }, []);

  const openEarnings = useCallback(() => {
    router.push('/earnings');
  }, []);

  /**
   * Describes a program to the share hook. Deals mint a per-deal campaign
   * link; the invite mints the user's single generic one. Both are
   * find-or-mint, so sharing works on a cold store.
   */
  const programFor = useCallback((deal: Deal | null): ShareProgram => ({
    id: deal?.id ?? INVITE_ID,
    resolveUrl: async () =>
      deal
        ? (await referralLinksStore.ensureCampaignLinkForDeal(deal.id, null)).public_url
        : (await referralLinksStore.ensureGenericLink()).public_url,
    message: deal ? buildShareMessage(deal) : buildInviteMessage(),
    // QR codes encode the deal's QR-TAGGED link (source='qr') so scans
    // attribute as link_source=qr, which source-conditioned bonuses read;
    // the plain campaign link above would be invisible to them.
    resolveQrUrl: deal
      ? async () => (await referralLinksStore.ensureQrLinkForDeal(deal.id)).public_url
      : undefined,
    qrCaption: deal ? `${deal.display_name} link` : 'Your MarketPryce invite link',
  }), []);

  // Referable deals only — single-action deals (referrals_enabled false)
  // stay on the Deals tab. Opted-in deals sort first so the deal the
  // user knows leads the list.
  const isOptedIn = (dealId: number) => dealsStore.actionsByDealId.has(dealId);
  const referableDeals = dealsStore.deals
    .filter((d) => d.referrals_enabled)
    .sort((a, b) => Number(isOptedIn(b.id)) - Number(isOptedIn(a.id)));

  // Null until /referrals/stats answers. The header link draws only then:
  // "$0 ›" while loading, or after a failed fetch, is a fake number where
  // a blank is the truth.
  const totalEarnedCents = referralStatsStore.data?.totals?.earnings_cents ?? null;

  // The invite program, published by the server in the same shape a deal
  // publishes. Its card is built from that payload exactly like a deal's is
  // built from the deal — no invite copy or amounts live in this file.
  const invite = referralStatsStore.data?.invite_program;

  // The tagline, story card and proof tiles, published by the server in the
  // same payload. Until stats answers there is nothing to draw — a blank is
  // the truth; canned copy would be a fake fallback.
  const marketing = referralStatsStore.data?.marketing;
  const inviteReferrerCents = invite?.referrer_cents ?? 0;
  const inviteInviteeCents = invite?.invitee_cents ?? 0;

  // === Branch: cold-load error (either list this tab is built from) ===
  const dealsFailedCold = Boolean(dealsStore.error) && !dealsStore.hasLoaded;
  if (dealsFailedCold || (referralsStore.refreshError && !referralsStore.loaded)) {
    return (
      <SafeAreaView style={styles.safe} edges={['top']}>
        <View style={styles.emptyContainer}>
          <ErrorState
            title="Couldn't load referrals"
            body="Pull down to refresh, or tap retry."
            onRetry={() => {
              void dealsStore.ensureLoaded();
              void referralsStore.refresh().catch(() => {});
            }}
            testID="refer-error"
          />
        </View>
      </SafeAreaView>
    );
  }

  // === Branch: nothing to share ===
  // Only once BOTH lists have answered: the deals list (programs) and the
  // stats payload (the invite program). Before that this would flash on
  // every cold start.
  const statsSettled = referralStatsStore.data !== null || referralStatsStore.error !== null;
  if (dealsStore.hasLoaded && statsSettled && referableDeals.length === 0 && !invite) {
    return (
      <SafeAreaView style={styles.safe} edges={['top']}>
        <View style={styles.emptyContainer}>
          <EmptyState
            title="No deals to share right now"
            body="New deals drop regularly. Check the Deals tab to keep earning in the meantime."
            cta={{ label: 'Browse Deals', onPress: () => router.push('/(tabs)/deals') }}
            testID="refer-empty"
          />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={referralsStore.refreshing}
            onRefresh={onRefresh}
            tintColor={colors.deepGreen}
          />
        }
      >
        <View style={styles.header}>
          <View style={styles.headerTitle}>
            <Heading size="5xl">Refer &amp; Earn</Heading>
            {marketing ? <Text style={styles.tagline}>{marketing.tagline}</Text> : null}
          </View>

          {/* Earnings demoted from a hero to a link: the number belongs to
              the Earnings screen, and a $0 slab was the loudest thing on
              the page for exactly the people this screen must convince. */}
          {totalEarnedCents !== null ? (
            <Pressable
              onPress={openEarnings}
              accessibilityRole="button"
              // Spoken to the cent: MoneyDisplay draws glyphs, not text.
              accessibilityLabel={`Your referral earnings, ${formatMoney(totalEarnedCents)}. See earnings.`}
              hitSlop={8}
              style={({ pressed }) => [styles.earnings, pressed && styles.earningsPressed]}
              testID="refer-earnings-link"
            >
              <Text style={styles.earningsLabel}>Your earnings</Text>
              <View style={styles.earningsValue}>
                <MoneyDisplay cents={totalEarnedCents} size="base" tone="onLight" />
                <ChevronRight size={16} color={colors.deepGreen} strokeWidth={2.25} />
              </View>
            </Pressable>
          ) : null}
        </View>

        {/* The tab's single dark moment: the poster always draws; the tap
            is live only with a video URL. Programs below are light cards:
            hero treatment used as list chrome flattens hierarchy instead
            of creating it. */}
        {marketing ? (
          <>
            <StoryCard story={marketing.story} testID="refer-story" />
            <ProofTiles points={marketing.proof_points} testID="refer-proof" />
          </>
        ) : null}

        {!dealsStore.hasLoaded ? (
          <ActivityIndicator color={colors.deepGreen} style={styles.loading} testID="refer-loading" />
        ) : null}

        {referableDeals.map((deal) => (
          <ProgramCard
            key={deal.id}
            name={deal.display_name}
            badge={deal.referrer_bonus_badge}
            // Real brand mark on a Frost plate. A Cool-Blue-tinted tile would
            // sit beside the green CTA — a forbidden pair (brand kit p.11).
            tile={
              <BrandLogo
                url={deal.brand_logo_url}
                size={48}
                fallback={
                  <Text style={styles.tileFallback}>
                    {deal.brand_name.slice(0, 2).toUpperCase()}
                  </Text>
                }
              />
            }
            // Amounts are the server's display_* when it sends them (the
            // invite-program backend resolves brand top-ups: SoFi Checking's
            // friend banks $50, only $20 of it ours), else the effective,
            // bonus-resolved cut fields every server already serves. Never a
            // column pick beyond that.
            referrerCents={referrerCents(deal)}
            userCents={userCents(deal)}
            // Same availability bar as the Deals tab ("No monthly limit" for
            // an uncapped deal), so "spots left" reads identically everywhere.
            availability={<DealAvailabilityBar deal={deal} compact style={styles.availability} />}
            shareLabel="Share my link"
            onShare={() => void share(programFor(deal))}
            onCopy={() => void copy(programFor(deal))}
            onShowQr={() => void showQr(programFor(deal))}
            onOpenDetail={() => openDeal(deal)}
            disabled={deal.full}
            sharing={sharingId === deal.id}
            testID={`refer-deal-${deal.slug}`}
          />
        ))}

        {/* Deal programs lead (SoFi's cut is the bigger, proven earner);
            the app invite follows. Locked with Jeff 2026-08-24. The card
            exists only when the server publishes the program: against a
            backend without it there is no invite to share, so no card —
            one app build serves both backends. */}
        {invite ? (
        <ProgramCard
          // The server names its own program through the same `eyebrow`
          // slot a deal uses. "MarketPryce" is the fallback because it is
          // the product's name, not copy — the brand on the tile beside it.
          name={invite.marketing_copy?.eyebrow ?? 'MarketPryce'}
          tile={<BrandTile size={48} combo="deepOnFrost" accessibilityLabel="MarketPryce" />}
          // Server-driven: the invitee's amount is zero until we actually
          // pay them, and ProgramCard drops that half of the line at zero,
          // so the card can never promise money that won't arrive.
          referrerCents={inviteReferrerCents}
          userCents={inviteInviteeCents}
          shareLabel="Share my invite link"
          onShare={() => void share(programFor(null))}
          onCopy={() => void copy(programFor(null))}
          onShowQr={() => void showQr(programFor(null))}
          onOpenDetail={openInvite}
          sharing={sharingId === INVITE_ID}
          testID="refer-invite"
        />
        ) : null}

        {/* The nudge callout deliberately does NOT live here. Its
            "$X left on the table" is friends_in_progress × the primary
            deal's referrer cut — a single-deal figure that means nothing
            on an index listing programs at different rates, and the card
            can't say which program those friends came from. It stays on
            the deal detail, where both are scoped. */}
      </ScrollView>

      <QrSheet
        isOpen={qr !== null}
        onClose={closeQr}
        url={qr?.url ?? null}
        caption={qr?.caption ?? 'Share link'}
      />

      <Toast message={toast} />
    </SafeAreaView>
  );
});

export default ReferralsTab;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: {
    padding: spacing.lg,
    gap: spacing.md,
    paddingBottom: spacing['3xl'],
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginBottom: spacing.xs,
  },
  headerTitle: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  tagline: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.textMuted,
  },
  earnings: {
    alignItems: 'flex-end',
    gap: 2,
    // Clears the 36pt heading's cap line so the label sits level with it.
    paddingTop: spacing.xs,
  },
  earningsPressed: { opacity: 0.6 },
  earningsLabel: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    lineHeight: 16,
    color: colors.textMuted,
  },
  earningsValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  availability: {
    marginTop: spacing.xs,
  },
  loading: {
    marginTop: spacing.lg,
  },
  tileFallback: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
  },
});
